import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  QrCode, Download, Plus, Search, Trash2, Eye, BarChart3,
  Link2, Smartphone, Monitor, Tablet, RefreshCw, Copy, ExternalLink
} from 'lucide-react';
import { cn } from '../lib/utils';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const QR_TYPES = [
  { value: 'url', label: 'Custom URL' },
  { value: 'website', label: 'Website' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'sms', label: 'SMS' },
  { value: 'wifi', label: 'WiFi' },
  { value: 'text', label: 'Text' },
];

const COLORS = [
  { value: '#000000', label: 'Black' },
  { value: '#1E293B', label: 'Slate' },
  { value: '#1D4ED8', label: 'Blue' },
  { value: '#059669', label: 'Emerald' },
  { value: '#7C3AED', label: 'Purple' },
  { value: '#DC2626', label: 'Red' },
];

const StatCard = ({ icon: Icon, label, value, color }) => (
  <Card className="bg-white border-gray-border" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-3 sm:p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-medium">{label}</p>
          <p className="text-xl sm:text-2xl font-bold text-text-primary mt-1">{value}</p>
        </div>
        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", color)}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </CardContent>
  </Card>
);

// ─── QR Code Card ───
const QRCard = ({ code, onView, onDelete }) => {
  const trackingUrl = `${API}/api/qr/s/${code.short_code}`;
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(trackingUrl);
    toast.success('Tracking URL copied!');
  };
  return (
    <Card className="bg-white border-gray-border hover:shadow-sm transition-shadow cursor-pointer" onClick={() => onView(code)} data-testid={`qr-card-${code.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-16 h-16 rounded-lg border border-gray-border flex items-center justify-center flex-shrink-0 bg-white p-1">
            <QRCodeSVG value={trackingUrl} size={56} fgColor={code.fg_color || '#000'} bgColor={code.bg_color || '#fff'} />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-text-primary truncate">{code.name}</h4>
            <div className="flex items-center gap-2 mt-1">
              <Badge className="text-[10px] bg-blue-100 text-blue-700">{code.qr_type}</Badge>
              {!code.active && <Badge className="text-[10px] bg-red-100 text-red-700">Inactive</Badge>}
            </div>
            <p className="text-xs text-gray-medium mt-1 truncate">{code.target_url || '—'}</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-gray-border">
          <div className="text-center">
            <p className="text-sm font-semibold text-text-primary">{code.scan_count || 0}</p>
            <p className="text-[10px] text-gray-medium">Scans</p>
          </div>
          <div className="text-center">
            <p className="text-sm font-semibold text-text-primary">{code.unique_scans || 0}</p>
            <p className="text-[10px] text-gray-medium">Unique</p>
          </div>
          <div className="text-center">
            <button onClick={handleCopy} className="text-xs text-emerald-brand hover:underline flex items-center gap-0.5 mx-auto">
              <Copy className="h-3 w-3" /> Copy
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between mt-2" onClick={e => e.stopPropagation()}>
          <p className="text-[10px] text-gray-medium truncate max-w-[60%]">/s/{code.short_code}</p>
          <Button variant="ghost" size="icon" className="h-6 w-6 hover:text-red-500" onClick={() => onDelete(code.id)}>
            <Trash2 className="h-3 w-3 text-gray-medium" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Create Dialog ───
const CreateQRDialog = ({ open, onOpenChange, onCreated }) => {
  const [form, setForm] = useState({ name: '', qr_type: 'url', target_url: '', fg_color: '#000000', bg_color: '#FFFFFF', notes: '' });
  const [saving, setSaving] = useState(false);
  const qrRef = useRef(null);

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.target_url.trim() && form.qr_type === 'url') { toast.error('URL is required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/qr/codes`, form);
      toast.success('QR Code saved!');
      onCreated();
      onOpenChange(false);
      setForm({ name: '', qr_type: 'url', target_url: '', fg_color: '#000000', bg_color: '#FFFFFF', notes: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    } finally { setSaving(false); }
  };

  const downloadPreview = () => {
    if (!form.target_url) return;
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    const canvas = document.createElement('canvas');
    canvas.width = 512; canvas.height = 512;
    const ctx = canvas.getContext('2d');
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = form.bg_color;
      ctx.fillRect(0, 0, 512, 512);
      ctx.drawImage(img, 0, 0, 512, 512);
      const link = document.createElement('a');
      link.download = `${form.name || 'qrcode'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <QrCode className="h-5 w-5 text-emerald-brand" /> Create QR Code
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
          <div className="space-y-3">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Summer Promo" className="mt-1" data-testid="qr-name" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.qr_type} onValueChange={v => setForm(f => ({ ...f, qr_type: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{QR_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Target URL *</Label>
              <Input value={form.target_url} onChange={e => setForm(f => ({ ...f, target_url: e.target.value }))} placeholder="https://..." className="mt-1" data-testid="qr-url" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Foreground</Label>
                <Select value={form.fg_color} onValueChange={v => setForm(f => ({ ...f, fg_color: v }))}>
                  <SelectTrigger className="mt-1 h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>{COLORS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Background</Label>
                <Input type="color" value={form.bg_color} onChange={e => setForm(f => ({ ...f, bg_color: e.target.value }))} className="mt-1 h-8 p-1" />
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center gap-3">
            <div ref={qrRef} className="p-4 bg-white rounded-lg border border-gray-border">
              <QRCodeSVG value={form.target_url || 'https://konnect.app'} size={160} fgColor={form.fg_color} bgColor={form.bg_color} />
            </div>
            <Button variant="outline" size="sm" className="gap-1" onClick={downloadPreview} disabled={!form.target_url}>
              <Download className="h-3 w-3" /> Download Preview
            </Button>
            {form.target_url && (
              <p className="text-[10px] text-gray-medium text-center">
                QR will auto-track scans via short link
              </p>
            )}
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-emerald-brand hover:bg-emerald-hover" onClick={handleSubmit} disabled={saving} data-testid="save-qr-btn">
            {saving ? 'Saving...' : 'Save QR Code'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Analytics Dialog ───
const AnalyticsDialog = ({ open, onOpenChange, code }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && code) {
      setLoading(true);
      axios.get(`${API}/api/qr/codes/${code.id}/analytics`)
        .then(res => setAnalytics(res.data))
        .catch(() => toast.error('Failed to load analytics'))
        .finally(() => setLoading(false));
    }
  }, [open, code]);

  const deviceIcons = { mobile: Smartphone, desktop: Monitor, tablet: Tablet };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-emerald-brand" /> QR Analytics: {code?.name}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-8"><RefreshCw className="h-6 w-6 animate-spin text-emerald-brand" /></div>
        ) : analytics ? (
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-emerald-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-emerald-brand">{analytics.total_scans}</p>
                <p className="text-xs text-emerald-600">Total Scans</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-blue-600">{analytics.unique_scans}</p>
                <p className="text-xs text-blue-600">Unique Scans</p>
              </div>
            </div>

            {Object.keys(analytics.device_breakdown || {}).length > 0 && (
              <div>
                <p className="text-sm font-medium text-text-primary mb-2">Device Breakdown</p>
                <div className="space-y-2">
                  {Object.entries(analytics.device_breakdown).map(([device, count]) => {
                    const DeviceIcon = deviceIcons[device] || Monitor;
                    const total = analytics.total_scans || 1;
                    const pct = Math.round((count / total) * 100);
                    return (
                      <div key={device} className="flex items-center gap-3">
                        <DeviceIcon className="h-4 w-4 text-gray-medium flex-shrink-0" />
                        <span className="text-xs text-text-primary capitalize w-16">{device}</span>
                        <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-brand/60 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs font-medium text-text-primary w-12 text-right">{count} ({pct}%)</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {analytics.recent_scans?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-text-primary mb-2">Recent Scans</p>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {analytics.recent_scans.map((s, i) => (
                    <div key={i} className="flex items-center justify-between text-xs p-1.5 bg-off-white rounded">
                      <span className="text-gray-medium capitalize">{s.device_type}</span>
                      <span className="text-gray-medium">{new Date(s.scanned_at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {analytics.total_scans === 0 && (
              <p className="text-sm text-gray-medium text-center py-4">No scans recorded yet. Share the short URL to start tracking.</p>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};


// ─── Main Page ───
export default function QRManagementPage() {
  const [dashboard, setDashboard] = useState(null);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [analyticsCode, setAnalyticsCode] = useState(null);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const [dashRes, codesRes] = await Promise.all([
        axios.get(`${API}/api/qr/dashboard`),
        axios.get(`${API}/api/qr/codes`),
      ]);
      setDashboard(dashRes.data);
      setCodes(codesRes.data.codes || []);
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this QR code?')) return;
    try {
      await axios.delete(`${API}/api/qr/codes/${id}`);
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const filtered = codes.filter(c => {
    if (filterType !== 'all' && c.qr_type !== filterType) return false;
    if (searchQuery && !c.name.toLowerCase().includes(searchQuery.toLowerCase()) && !(c.target_url || '').toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="h-8 w-8 animate-spin text-emerald-brand" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="qr-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">QR Codes<PageInfoTooltip pageKey="qr-code-management" /></h1>
          <p className="text-gray-medium">Create, track, and analyze your QR codes</p>
        </div>
        <Button className="bg-emerald-brand hover:bg-emerald-hover gap-2" onClick={() => setCreateOpen(true)} data-testid="create-qr-btn">
          <Plus className="h-4 w-4" /> New QR Code
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={QrCode} label="Total Codes" value={dashboard?.total_codes || 0} color="bg-emerald-100 text-emerald-700" />
        <StatCard icon={QrCode} label="Active" value={dashboard?.active_codes || 0} color="bg-blue-100 text-blue-700" />
        <StatCard icon={Eye} label="Total Scans" value={dashboard?.total_scans || 0} color="bg-purple-100 text-purple-700" />
        <StatCard icon={Eye} label="Unique Scans" value={dashboard?.unique_scans || 0} color="bg-amber-100 text-amber-700" />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="codes" className="space-y-4">
        <TabsList className="overflow-x-auto flex-nowrap bg-white border border-gray-border w-full sm:w-auto">
          <TabsTrigger value="codes" data-testid="tab-codes" className="text-xs sm:text-sm">My QR Codes ({codes.length})</TabsTrigger>
          <TabsTrigger value="insights" data-testid="tab-insights" className="text-xs sm:text-sm">Insights</TabsTrigger>
        </TabsList>

        {/* ─── Codes Tab ─── */}
        <TabsContent value="codes" className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-medium" />
              <Input placeholder="Search QR codes..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9" data-testid="qr-search" />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full sm:w-[140px]"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {QR_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {filtered.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map(c => (
                <QRCard
                  key={c.id}
                  code={c}
                  onView={c => { setAnalyticsCode(c); setAnalyticsOpen(true); }}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          ) : (
            <Card className="bg-white border-gray-border">
              <CardContent className="p-8 text-center">
                <QrCode className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-medium">{searchQuery ? 'No QR codes match your search' : 'No QR codes created yet'}</p>
                <Button className="bg-emerald-brand hover:bg-emerald-hover mt-4" onClick={() => setCreateOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" /> Create QR Code
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ─── Insights Tab ─── */}
        <TabsContent value="insights" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Top Performing */}
            <Card className="bg-white border-gray-border" data-testid="top-qr-codes">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-heading flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-emerald-brand" /> Top Performing QR Codes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(dashboard?.top_codes || []).length > 0 ? (
                  <div className="space-y-3">
                    {dashboard.top_codes.map((c, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-off-white">
                        <span className="text-sm font-bold text-gray-medium w-5">{i + 1}</span>
                        <QrCode className="h-4 w-4 text-emerald-brand flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-text-primary truncate">{c.name}</p>
                          <p className="text-[10px] text-gray-medium">/s/{c.short_code}</p>
                        </div>
                        <Badge className="bg-emerald-brand/10 text-emerald-brand text-xs">{c.scan_count} scans</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-medium text-center py-4">Create QR codes to see performance data</p>
                )}
              </CardContent>
            </Card>

            {/* By Type */}
            <Card className="bg-white border-gray-border" data-testid="qr-by-type">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-heading flex items-center gap-2">
                  <Link2 className="h-4 w-4 text-blue-500" /> QR Codes by Type
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(dashboard?.by_type || []).length > 0 ? (
                  <div className="space-y-3">
                    {dashboard.by_type.map((t, i) => {
                      const typeLabel = QR_TYPES.find(qt => qt.value === t.type)?.label || t.type;
                      const max = Math.max(...dashboard.by_type.map(x => x.count));
                      return (
                        <div key={i} className="flex items-center gap-3">
                          <span className="text-xs text-text-primary w-20 capitalize">{typeLabel}</span>
                          <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-400 rounded-full" style={{ width: `${(t.count / max) * 100}%` }} />
                          </div>
                          <span className="text-xs font-medium text-text-primary w-6 text-right">{t.count}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-medium text-center py-4">No data yet</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* How it works */}
          <Card className="bg-white border-gray-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-heading">How Tracking Works</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                  <p className="text-xs font-medium text-emerald-700 mb-1">1. Create & Share</p>
                  <p className="text-xs text-emerald-600">Create a QR code with a short URL. Share the generated branded link.</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-xs font-medium text-blue-700 mb-1">2. Auto-Tracking</p>
                  <p className="text-xs text-blue-600">Every scan is tracked with device type. Unique scans are counted per IP.</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                  <p className="text-xs font-medium text-purple-700 mb-1">3. Analyze</p>
                  <p className="text-xs text-purple-600">View scan analytics, device breakdown, and performance trends per code.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <CreateQRDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={fetchData} />
      <AnalyticsDialog open={analyticsOpen} onOpenChange={setAnalyticsOpen} code={analyticsCode} />
    </div>
  );
}
